import { JSX, useState } from "react";
import { DatePicker, Table, TableColumnsType } from "antd";
import { useQuery } from "react-query";
import dayjs, { Dayjs } from "dayjs";
import Text from "../../components/Text/Text";
import { GreyTableContainer } from "../../components/Table/GreyTable";
import { compactAmountFormatter } from "../../utility/Formatter";
import { getTierTransaction, TierTransactionItem } from "../../datasource/TierTransactionDatasource";
import TierTablePagination, { PAGE_SIZE_OPTIONS } from "../ShopTier/TierTablePagination";

// Fallback when an ICPL row has no stored actionName (mirrors TIER_TRANSACTION_ACTION_NAME).
const INVOICE_STATUS_LABEL: Record<string, string> = {
  APPROVE: "สั่งซื้อสินค้า",
  CN: "แก้ไขยอดจาก CN",
  DN: "แก้ไขยอดจาก DN",
  CANCEL: "ยกเลิกคำสั่งซื้อ",
};

interface Props {
  customerCompanyId: number;
  company: string;
}

// "ICPL" accrues baht from invoices; ICPF/ICPI accrue tonnes from shipments.
function describeRow(row: TierTransactionItem, company: string): { title: string; amount: string } {
  if (company === "ICPL") {
    const sign = row.action === "DECREASE" ? "-" : "+";
    return {
      title: row.actionName || INVOICE_STATUS_LABEL[row.status ?? ""] || "-",
      // compactAmountFormatter already appends the unit (K฿ / M฿ for ICPL).
      amount: `${sign} ${compactAmountFormatter(company, row.amount ?? 0)}`,
    };
  }
  const sign = row.isCanceled ? "-" : "+";
  return {
    title: row.isCanceled ? "ยกเลิกคำสั่งซื้อ" : "กดรับสินค้า",
    // Non-ICPL branch of compactAmountFormatter formats tonnes ("… ตัน").
    amount: `${sign} ${compactAmountFormatter(company, row.sumQuantity ?? 0)}`,
  };
}

function AccrualMovementTab({ customerCompanyId, company }: Props): JSX.Element {
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(PAGE_SIZE_OPTIONS[0]);

  // Backend filters created_at BETWEEN start AND end, so send whole days.
  const startDate = range?.[0] ? range[0].startOf("day").toISOString() : undefined;
  const endDate = range?.[1] ? range[1].endOf("day").toISOString() : undefined;

  const { data, isLoading } = useQuery(
    ["tier-transaction", customerCompanyId, company, startDate, endDate, page, take],
    () => getTierTransaction({ customerCompanyId, company, startDate, endDate, page, take }),
    { enabled: !!customerCompanyId && !!company, refetchOnWindowFocus: false, keepPreviousData: true },
  );
  const rows: TierTransactionItem[] = data?.responseData?.data ?? [];
  const total: number = data?.responseData?.count ?? 0;

  const columns: TableColumnsType<TierTransactionItem> = [
    {
      title: "วันที่",
      dataIndex: company === "ICPL" ? "createdAt" : "pdoDate",
      key: company === "ICPL" ? "createdAt" : "pdoDate",
      width: "25%",
      render: (v: string) => (
        <Text fontFamily="Sarabun" fontSize={16} color="Text2">
          {v ? dayjs(v).locale("th").format("D MMM BBBB, HH:mm น.") : "-"}
        </Text>
      ),
    },
    {
      title: "รายการ",
      key: "title",
      render: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text fontFamily="Sarabun" fontSize={16} color="Text2">{describeRow(row, company).title}</Text>
          {row.orderNo && (
            <Text fontFamily="Sarabun" fontSize={13} color="Text3">Order NO. {row.orderNo}</Text>
          )}
          {row.invoiceNo && (
            <Text fontFamily="Sarabun" fontSize={13} color="Text3">INV NO. {row.invoiceNo}</Text>
          )}
        </div>
      ),
    },
    {
      title: "ยอด",
      key: "amount",
      width: "30%",
      render: (_, row) => (
        <Text fontFamily="Sarabun" fontSize={16} color="Text2">{describeRow(row, company).amount}</Text>
      ),
    },
  ];

  return (
    <div>
      <div style={{ width: 300, marginBottom: 16 }}>
        <DatePicker.RangePicker
          value={range}
          onChange={(value) => {
            setRange(value as [Dayjs | null, Dayjs | null] | null);
            setPage(1);
          }}
          format="DD/MM/BBBB"
          placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
          style={{ width: "100%", height: 40, fontFamily: "Sarabun" }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Text fontFamily="Sarabun" fontSize={16} color="Text2">ทั้งหมด {total} รายการ</Text>
      </div>
      <GreyTableContainer>
        <Table
          rowKey={(r) => r.tierInvoiceTransactionId ?? r.tierShipmentId ?? `${r.orderNo}-${r.createdAt}`}
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          pagination={false}
        />
      </GreyTableContainer>
      <TierTablePagination
        current={page}
        pageSize={take}
        total={total}
        onChange={setPage}
        onPageSizeChange={(size) => {
          setTake(size);
          setPage(1);
        }}
      />
    </div>
  );
}

export default AccrualMovementTab;
